import React from 'react'
import { Box, Stack, type StackProps } from '@mui/material'
import {
    ActionDrawerTitle,
    ActionDrawerActiveItemDescription,
    type ActionDrawerActiveItemDescriptionProps,
    ActionDrawerSubmitButtons,
    ActionDrawerAlert,
} from './ui-action-drawer'
import { RhfDebug } from '@edge/spark/feature-mui-rhf-inputs'
import { useTableContext } from '@edge/spark/io-react-table'

export type PaginatedFormWrapperProps = {
    workflowTitle: string
    /** Item title */
    title: ActionDrawerActiveItemDescriptionProps['title']
    subtitle: ActionDrawerActiveItemDescriptionProps['subtitle']
    descriptionList: ActionDrawerActiveItemDescriptionProps['descriptionList']
    extraIcons?: ActionDrawerActiveItemDescriptionProps['extraIcons']
    onClose?: () => void
    loading?: boolean
    disabled?: boolean
    isSubmitting?: boolean
} & { containerProps?: StackProps } & {
    children: React.ReactNode
}

/** Generic ActionDrawer structure for single item pages. Should cover initial use cases, but should be extended as needed.
 *
 * Includes:
 * Item description
 * <-- chidlren go here (w/ injected `item` prop if needed) -->
 * Save buttons
 */
export const PaginatedFormWrapper = ({
    workflowTitle,
    title,
    subtitle,
    descriptionList,
    extraIcons,
    children,
    onClose,
    loading,
    isSubmitting,
}: PaginatedFormWrapperProps) => {
    const activeRowState = useTableContext((s) => s.getActiveRowState())

    const rowStatus = activeRowState?.status?.toString()
    const rowMessage = activeRowState?.message?.toString()
    const hasError = rowStatus === 'error'
    const showLoadingSpinnner = loading || isSubmitting
    const disableFormElements = loading || isSubmitting
    const disableSubmitButtons = loading || isSubmitting

    return (
        <Stack sx={{ display: 'flex', flex: 1, gap: 1 }}>
            <ActionDrawerTitle title={workflowTitle} onClose={onClose} loading={showLoadingSpinnner} />

            <ActionDrawerActiveItemDescription
                title={title}
                subtitle={subtitle}
                descriptionList={descriptionList}
                extraIcons={extraIcons}
            />

            {hasError && rowMessage && <ActionDrawerAlert message={rowMessage} />}

            <Box flex={1}>
                {/* Inject prop into child components */}
                {React.Children.map(children, (child) => {
                    if (React.isValidElement(child)) {
                        // item' does not exist in type 'Partial<unknown> & Attributes'
                        // @ts-expect-error TODO: fix this
                        return React.cloneElement(child, { disabled: disableFormElements })
                    }
                    return child
                })}
            </Box>

            <ActionDrawerSubmitButtons disabled={disableSubmitButtons} />
            <RhfDebug />
        </Stack>
    )
}
